/**
 * 订单 Service层 - 业务逻辑层 + Mock 适配
 * 页面组件只调用此 Service 层,不直接调用 API
 */

import { config } from '../../config';
import * as orderMock from '../../mocks/order';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';
import { CHANNEL_OPTIONS } from '../../constants/order';
import type { 
  ApiResponse, 
  PageData
} from '../../types/common';
import type {
  OrderDTO,
  OrderDetailVO,
  ReturnOrderDetailVO,
  ShortageOrderViewDTO,
  QueryOrderPageRequest,
  QueryReturnOrderPageRequest,
  QueryShortageOrderPageRequest,
  SubstituteRecordViewDTO,
  QuerySubstituteRecordPageRequest
} from '../../types/order/order';

/**
 * 分页查询订单列表
 */
export async function pageOrder(params: QueryOrderPageRequest): Promise<ApiResponse<PageData<OrderDTO>>> {
  if (config.mock) {
    return orderMock.mockPageOrder(params);
  }

  return httpClient.post(API_ENDPOINTS.ORDER.PAGE, params);
}

/**
 * 查询订单详情
 */
export async function getOrderByOrderNo(orderNo: string): Promise<ApiResponse<OrderDetailVO>> {
  if (config.mock) {
    return orderMock.mockGetOrderByOrderNo(orderNo);
  }

  return httpClient.get(`${API_ENDPOINTS.ORDER.BASE}/${orderNo}`);
}

/**
 * 获取订单渠道下拉选项（固定来源于 CHANNEL_OPTIONS 常量，不再调用后端接口）
 */
export async function getChannelOptions(): Promise<ApiResponse<Array<{ value: string; label: string }>>> {
  return { success: true, data: CHANNEL_OPTIONS } as ApiResponse<Array<{ value: string; label: string }>>;
}

/**
 * 获取退货订单筛选下拉选项（从实际数据中获取）
 */
export async function getReturnOrderOptions(): Promise<ApiResponse<{
  orderTypes: Array<{ value: string; label: string }>;
  channels: Array<{ value: string; label: string }>;
  paymentTypes: Array<{ value: string; label: string }>;
}>> {
  return httpClient.post(API_ENDPOINTS.ORDER.RETURN_ORDER_OPTION);
}

/**
 * 分页查询退货订单列表
 */
export async function pageReturnOrder(params: QueryReturnOrderPageRequest): Promise<ApiResponse<PageData<OrderDTO>>> {
  if (config.mock) {
    return orderMock.mockPageReturnOrder(params);
  }

  return httpClient.post(API_ENDPOINTS.ORDER.RETURN_ORDER_PAGE, params);
}

/**
 * 查询退货订单详情（仅退货订单专用，不含拣选明细 / 替换记录）
 */
export async function getReturnOrderByOrderNo(orderNo: string): Promise<ApiResponse<ReturnOrderDetailVO>> {
  if (config.mock) {
    return orderMock.mockGetOrderByOrderNo(orderNo) as unknown as Promise<ApiResponse<ReturnOrderDetailVO>>;
  }

  return httpClient.get(`${API_ENDPOINTS.ORDER.RETURN_ORDER_BASE}/${orderNo}`);
}

/**
 * 分页查询缺货订单列表
 */
export async function pageShortageOrder(params: QueryShortageOrderPageRequest): Promise<ApiResponse<PageData<ShortageOrderViewDTO>>> {
  if (config.mock) {
    return orderMock.mockPageShortageOrder(params);
  }

  return httpClient.post(API_ENDPOINTS.ORDER.SHORTAGE_ORDER_PAGE, params);
}

/**
 * 查询缺货订单详情
 */
export async function getShortageOrderByOrderNo(orderNo: string): Promise<ApiResponse<ShortageOrderViewDTO>> {
  if (config.mock) {
    return orderMock.mockGetShortageOrderByOrderNo(orderNo);
  }

  return httpClient.get(`${API_ENDPOINTS.ORDER.SHORTAGE_ORDER_BASE}/${orderNo}`);
}

/**
 * 分页查询替换记录列表
 */
export async function pageSubstituteRecord(params: QuerySubstituteRecordPageRequest): Promise<ApiResponse<PageData<SubstituteRecordViewDTO>>> {
  if (config.mock) {
    return orderMock.mockPageSubstituteRecord(params);
  }

  return httpClient.post(API_ENDPOINTS.ORDER.SUBSTITUTE_RECORD_PAGE, params);
}

/**
 * 导出订单列表
 */
export async function exportOrder(params: QueryOrderPageRequest): Promise<ApiResponse<{ downloadUrl: string; fileName: string }>> {
  if (config.mock) {
    return orderMock.mockExportOrder(params);
  }
  return httpClient.post(API_ENDPOINTS.ORDER.EXPORT, params);
}

/**
 * 导出退货订单列表
 */
export async function exportReturnOrder(params: QueryReturnOrderPageRequest): Promise<ApiResponse<{ downloadUrl: string; fileName: string }>> {
  if (config.mock) {
    return orderMock.mockExportReturnOrder(params);
  }
  return httpClient.post(API_ENDPOINTS.ORDER.RETURN_ORDER_EXPORT, params);
}

/**
 * 导出缺货订单列表
 */
export async function exportShortageOrder(params: QueryShortageOrderPageRequest): Promise<ApiResponse<{ downloadUrl: string; fileName: string }>> {
  if (config.mock) {
    return orderMock.mockExportShortageOrder(params);
  }
  return httpClient.post(API_ENDPOINTS.ORDER.SHORTAGE_ORDER_EXPORT, params);
}

/**
 * 导出替换记录
 */
export async function exportSubstituteRecord(params: QuerySubstituteRecordPageRequest): Promise<ApiResponse<{ downloadUrl: string; fileName: string }>> {
  if (config.mock) {
    return orderMock.mockExportSubstituteRecord(params);
  }
  return httpClient.post(API_ENDPOINTS.ORDER.SUBSTITUTE_RECORD_EXPORT, params);
}